import React, { useEffect, useState } from "react";
import { SweetAlertShow } from "../master/Master";
import { studentsListOnly } from "../../api/Student";
import { getBatchList } from "../../api/BatchService";
import {
  distributionKitAssign,
  distributionKitItemAssign,
} from "../../api/DistributionKit";

/**
 * Bulk-assign a distribution kit (or a specific kit item) to multiple
 * students or batches in one go.
 *
 * Props:
 *  - kit: { id, name, quantity, final_price, is_free }  (required)
 *  - item: { id, name, quantity, final_price, is_free } (optional — assigns
 *           this item instead of the whole kit when provided)
 *  - onClose: () => void
 *  - onAssigned: () => void   called after a successful assignment
 */
export default function AssignKitModal({ kit, item, onClose, onAssigned }) {
  const target = item || kit;

  const [assignableType, setAssignableType] = useState("student");
  const [search, setSearch] = useState("");
  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [discountAmount, setDiscountAmount] = useState("");
  const [discountRemark, setDiscountRemark] = useState("");

  const fetchList = async () => {
    setListLoading(true);

    try {
      let addUrl = search ? `search=${encodeURIComponent(search)}` : "";

      const response =
        assignableType === "student"
          ? await studentsListOnly(addUrl)
          : await getBatchList(addUrl);

      const ok = response?.data?.success ?? response?.data?.status;

      if (ok) {
        setList(response?.data?.data?.data || response?.data?.data || []);
      } else {
        setList([]);
      }
    } catch (error) {
      SweetAlertShow("error", "Error", error.message);
      setList([]);
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    setSelectedIds([]);
    setDiscountAmount("");
    setDiscountRemark("");
  }, [assignableType]);

  useEffect(() => {
    const delay = setTimeout(fetchList, 300);
    return () => clearTimeout(delay);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignableType, search]);

  const displayName = (row) => {
    if (assignableType === "batch") {
      return row.title || row.name || `Batch #${row.id}`;
    }

    return `${row.name || row.student_name || "Student"}${
      row.registration_no ? ` (${row.registration_no})` : ""
    }`;
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === list.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(list.map((row) => row.id));
    }
  };

  const handleAssign = async () => {
    if (selectedIds.length === 0) {
      SweetAlertShow(
        "info",
        "Warning",
        "Select at least one record to assign.",
      );
      return;
    }

    const totalAmountPerAssignment =
      Number(target?.final_price || 0) * (Number(quantity) || 1);

    const discount = Number(discountAmount || 0);

    if (discount > totalAmountPerAssignment) {
      SweetAlertShow(
        "info",
        "Invalid Discount",
        "Discount cannot exceed the amount per assignment.",
      );
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        assignable_type: assignableType,
        assignable_ids: selectedIds,
        quantity: Number(quantity) || 1,
        discount_amount: Number(discountAmount || 0),
        discount_remark: discountRemark.trim() || null,
      };

      const response = item
        ? await distributionKitItemAssign(kit.id, item.id, payload)
        : await distributionKitAssign(kit.id, payload);

      if (response.data.success) {
        SweetAlertShow("success", "Successfully", response.data.message);
        onAssigned?.();
        onClose();
      } else {
        SweetAlertShow("info", "Warning", response.data.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message || error.message,
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex={-1} role="dialog">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 shadow-lg">
            {/* HEADER */}
            <div className="modal-header border-bottom px-4 py-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="d-flex align-items-center justify-content-center rounded-3 bg-label-primary"
                  style={{ width: 44, height: 44 }}
                >
                  <i className="ri-user-add-line fs-5 text-primary" />
                </div>

                <div>
                  <h5 className="modal-title fw-bold mb-0">
                    Assign {item ? item.name : kit.name}
                  </h5>
                  <small className="text-muted">
                    {target?.is_free
                      ? "Free"
                      : `₹${target?.final_price ?? 0} per assignment`}{" "}
                    · {target?.quantity ?? 0} in stock
                  </small>
                </div>
              </div>

              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            {/* BODY */}
            <div className="modal-body p-4">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                {/* Type toggle */}
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${
                      assignableType === "student"
                        ? "btn-primary"
                        : "btn-label-primary"
                    }`}
                    onClick={() => setAssignableType("student")}
                  >
                    <i className="ri-user-line me-1" />
                    Students
                  </button>

                  <button
                    type="button"
                    className={`btn btn-sm ${
                      assignableType === "batch"
                        ? "btn-primary"
                        : "btn-label-primary"
                    }`}
                    onClick={() => setAssignableType("batch")}
                  >
                    <i className="ri-team-line me-1" />
                    Batches
                  </button>
                </div>

                <div className="input-group ms-auto" style={{ maxWidth: 260 }}>
                  <span className="input-group-text bg-transparent">
                    <i className="ri-search-line" />
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder={`Search ${
                      assignableType === "student" ? "students" : "batches"
                    }...`}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>

                <div style={{ width: 110 }}>
                  <input
                    type="number"
                    min={1}
                    className="form-control form-control-sm"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    title="Quantity per assignment"
                  />
                </div>
              </div>

              <div className="row g-3 mb-3">
                {/* Discount */}
                {!target?.is_free && (
                  <div className="col-md-4">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        className="form-control"
                        placeholder="Discount Amount"
                        value={discountAmount}
                        onChange={(e) => setDiscountAmount(e.target.value)}
                      />
                      <label>Discount Amount</label>
                    </div>
                  </div>
                )}

                {/* Discount Remark */}
                {!target?.is_free && (
                  <div className="col-md-8">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Discount Remark"
                        value={discountRemark}
                        onChange={(e) => setDiscountRemark(e.target.value)}
                      />
                      <label>Discount Remark</label>
                    </div>
                  </div>
                )}
              </div>

              {!target?.is_free && (
                <div className="alert alert-light border d-flex align-items-center justify-content-between mb-3">
                  <div>
                    <div className="small text-muted">
                      Amount per assignment
                    </div>
                    <div className="fw-semibold">
                      ₹
                      {Math.max(
                        0,
                        Number(target?.final_price || 0) *
                          Number(quantity || 1) -
                          Number(discountAmount || 0),
                      ).toFixed(2)}
                    </div>
                  </div>

                  {Number(discountAmount || 0) > 0 && (
                    <div className="text-end">
                      <div className="small text-muted">
                        Discount per assignment
                      </div>
                      <div className="fw-semibold text-danger">
                        - ₹{Number(discountAmount || 0).toFixed(2)}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* List */}
              <div
                className="border rounded-3"
                style={{ maxHeight: 320, overflowY: "auto" }}
              >
                <div className="d-flex align-items-center justify-content-between border-bottom px-3 py-2 bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <input
                      className="form-check-input mt-0 flex-shrink-0"
                      type="checkbox"
                      id="selectAll"
                      checked={
                        list.length > 0 && selectedIds.length === list.length
                      }
                      onChange={toggleSelectAll}
                    />
                    <label
                      className="mb-0 small"
                      htmlFor="selectAll"
                      style={{ cursor: "pointer" }}
                    >
                      Select All
                    </label>
                  </div>

                  <span className="badge bg-label-primary">
                    {selectedIds.length} selected
                  </span>
                </div>

                {listLoading && (
                  <div className="text-center py-4 text-muted small">
                    Loading...
                  </div>
                )}

                {!listLoading && list.length === 0 && (
                  <div className="text-center py-4 text-muted small">
                    No {assignableType === "student" ? "students" : "batches"}{" "}
                    found
                  </div>
                )}

                {!listLoading &&
                  list.map((row) => (
                    <div
                      key={row.id}
                      className="d-flex align-items-center gap-2 px-3 py-2 border-bottom"
                      onClick={() => toggleSelect(row.id)}
                      style={{ cursor: "pointer" }}
                    >
                      <input
                        className="form-check-input mt-0 flex-shrink-0"
                        type="checkbox"
                        checked={selectedIds.includes(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <label
                        className="mb-0 w-100"
                        style={{ cursor: "pointer" }}
                      >
                        {displayName(row)}
                      </label>
                    </div>
                  ))}
              </div>
            </div>

            {/* FOOTER */}
            <div className="modal-footer border-top px-4 py-3">
              <button
                type="button"
                className="btn btn-label-secondary"
                onClick={onClose}
              >
                Cancel
              </button>

              <button
                type="button"
                className="btn btn-primary px-4"
                disabled={submitting || selectedIds.length === 0}
                onClick={handleAssign}
              >
                <i className="ri-check-line me-1" />
                {submitting
                  ? "Assigning..."
                  : `Assign to ${selectedIds.length || ""} ${
                      assignableType === "student" ? "Student(s)" : "Batch(es)"
                    }`}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="modal-backdrop fade show" />
    </>
  );
}
